/**
 * API client.
 *
 * Every write is signed here, using the shared payload builders so the bytes
 * match what the server verifies. If you are adding an endpoint that mutates
 * anything, it signs — there is no unsigned write path, deliberately.
 */

import {
  canonicalize,
  consentPayload,
  contributionPayload,
  registrationPayload,
} from '@omnialera/ori-protocol';
import { freshNonce, loadIdentity } from './identity.js';

const BASE = (import.meta.env?.VITE_API_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || code || `HTTP ${status}`);
    this.status = status;
    this.code = code;
  }
}

const encoder = new TextEncoder();

async function request(path, { method = 'GET', body, headers = {}, signal } = {}) {
  // With no VITE_API_URL the base is empty, so every request goes to the app's
  // own origin — where the single-page catch-all answers /v1/anything with
  // index.html and a 200. A caller then receives what looks like a successful
  // response and works with the shell of a web page as if it were data. Better
  // to say plainly that there is no service than to hand back a convincing
  // impostor.
  if (!BASE) {
    throw new ApiError(0, 'not_configured', 'No Ori service is configured for this build.');
  }

  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers: { ...(body ? { 'content-type': 'application/json' } : {}), ...headers },
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network_error', 'Could not reach the Ori service.');
  }

  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { raw: text };
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, payload?.error ?? 'http_error', payload?.message);
  }
  return payload;
}

/** Sign a payload with the device key and return the wire body. */
async function signed(buildPayload, fields) {
  const identity = await loadIdentity();
  const base = { nonce: freshNonce(), issuedAt: new Date().toISOString(), ...fields };
  const payload = buildPayload({ deviceId: identity.deviceId, ...base });
  const signature = await identity.signBytes(encoder.encode(canonicalize(payload)));
  return { ...payload, signature };
}

export const api = {
  baseUrl: BASE,
  configured: Boolean(BASE),

  async registerDevice({ monkTone, appVersion, engineVersion } = {}) {
    const identity = await loadIdentity();
    const base = {
      publicKey: identity.publicKey,
      nonce: freshNonce(),
      issuedAt: new Date().toISOString(),
    };
    const signature = await identity.signBytes(encoder.encode(canonicalize(registrationPayload(base))));
    return request('/v1/devices', {
      method: 'POST',
      body: { ...base, signature, monkTone, appVersion, engineVersion },
    });
  },

  async setConsent(scope, granted) {
    const body = await signed(consentPayload, { scope, granted });
    return request('/v1/consent', { method: 'POST', body });
  },

  async getConsent(deviceId) {
    return request(`/v1/consent/${encodeURIComponent(deviceId)}`);
  },

  async contribute(reading) {
    const body = await signed(contributionPayload, { reading });
    return request('/v1/contributions', { method: 'POST', body });
  },

  async deviceSummary(deviceId) {
    return request(`/v1/devices/${encodeURIComponent(deviceId)}/summary`);
  },

  async corpus() {
    return request('/v1/corpus');
  },

  async recentContributions(limit = 30) {
    return request(`/v1/contributions/recent?limit=${limit}`);
  },

  async operatorOverview(token) {
    return request('/v1/operator/overview', { headers: { authorization: `Bearer ${token}` } });
  },

  async operatorPerformance(token) {
    return request('/v1/operator/performance', { headers: { authorization: `Bearer ${token}` } });
  },

  async operatorExports(token) {
    return request('/v1/operator/exports', { headers: { authorization: `Bearer ${token}` } });
  },

  async operatorRefusals(token) {
    return request('/v1/operator/refusals', { headers: { authorization: `Bearer ${token}` } });
  },

  async operatorRevocations(token) {
    return request('/v1/operator/revocations', { headers: { authorization: `Bearer ${token}` } });
  },

  /**
   * Subscribe to live corpus coverage.
   *
   * Falls back to polling when EventSource is unavailable or the stream keeps
   * dying — a counter that silently stops updating is worse than one that
   * updates slowly, and on mobile networks the stream will occasionally drop.
   */
  subscribeCorpus(onCoverage, onError) {
    if (!BASE) return () => {};

    let closed = false;
    let source = null;
    let pollTimer = null;
    let failures = 0;

    const poll = async () => {
      if (closed) return;
      try {
        onCoverage(await this.corpus());
      } catch (err) {
        onError?.(err);
      }
      if (!closed) pollTimer = setTimeout(poll, 15000);
    };

    const startPolling = () => {
      if (pollTimer || closed) return;
      poll();
    };

    if (typeof EventSource === 'undefined') {
      startPolling();
    } else {
      const connect = () => {
        if (closed) return;
        source = new EventSource(`${BASE}/v1/corpus/stream`);
        source.addEventListener('coverage', (event) => {
          failures = 0;
          try {
            onCoverage(JSON.parse(event.data));
          } catch (err) {
            onError?.(err);
          }
        });
        source.onerror = () => {
          source?.close();
          source = null;
          if (closed) return;
          failures += 1;
          if (failures >= 3) {
            startPolling();
            return;
          }
          setTimeout(connect, Math.min(8000, 500 * 2 ** failures));
        };
      };
      connect();
    }

    return () => {
      closed = true;
      source?.close();
      if (pollTimer) clearTimeout(pollTimer);
    };
  },
};

export default api;
